import { useContext, useEffect, useRef } from 'react'
import { MultiLanguageString, SessionTrait, TraitValue } from 'types'
import { useElementSize } from 'utils'
import { panelPadding } from '../../../utils/globals'
import { useScale } from '../../../utils/scaleUtils'
import { getSelectionIndex, makeSelection } from '../../../utils/selectionUtils'
import AnimatedCheckmark from '../../AnimatedCheckmark'
import {
  ExplorerConfigContext,
  ISessionContext,
  SelectionCountsContext,
  SessionContext,
} from '../../providers'
import { SDivider, SKitIcon, SText, SView, SViewProps } from '../../scaled'
import { TraitSelectorProps } from './'

export default function TraitListSelector({
  group,
  onSelect,
  selections,
  getLanguageValue,
}: TraitSelectorProps) {
  const { alphaScroll, showFrequentSelections, title, values } = group
  const scale = useScale()
  const { status } = useContext(SessionContext)

  const { ref: frequentRef, height: frequentHeight } = useElementSize()
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (status !== 'active') scrollTo('A')
  }, [status])

  function scrollTo(alphaCharacter: string) {
    if (alphaCharacter === 'A') return scrollRef.current?.scroll({ top: 0 })

    let char = alphaCharacter
    let missing = false
    for (let i = 0; i < 26; i++) {
      let startIndex = values.findIndex(v => getLanguageValue(v.title).startsWith(char))
      if (char === 'Z') startIndex = values.length - 1

      if (startIndex > -1) {
        const index = startIndex - (missing ? 1 : 0)
        const initialHeight = frequentHeight || 0
        const y = initialHeight + index * listItemHeight * scale
        scrollRef.current?.scroll({ top: y })
        break
      } else {
        missing = true
        char = String.fromCharCode(char.charCodeAt(0) + 1)
      }
    }
  }

  return (
    <SView flex={1} overflow="hidden">
      <SDivider />
      <SView direction="row" overflow="hidden" flex={1}>
        <SView ref={scrollRef} overflow="auto" style={{ flex: 1 }}>
          <SView ref={frequentRef}>
            {showFrequentSelections && (
              <FrequentSelections
                getLanguageValue={getLanguageValue}
                groupTitle={title!}
                options={values}
                onSelect={onSelect}
                selections={selections}
              />
            )}

            <SView h={8} />
          </SView>

          {values.map((v, i) => (
            <ListItem
              key={i}
              getLanguageValue={getLanguageValue}
              option={v}
              onSelect={() => onSelect(v)}
              isSelected={selections.some(s =>
                s.traitId ? s.traitId === v.id : s.title === getLanguageValue(v.title)
              )}
            />
          ))}
        </SView>
        {alphaScroll && <AlphaScroller onFilter={scrollTo} />}
      </SView>
    </SView>
  )
}

interface FrequentSelectionsProps extends Omit<SViewProps, 'onSelect'> {
  getLanguageValue: ISessionContext['getLanguageValue']
  groupTitle: MultiLanguageString
  onSelect: (selection: TraitValue) => void
  options: TraitValue[]
  selections: SessionTrait[]
}

function FrequentSelections({
  getLanguageValue,
  groupTitle,
  options,
  onSelect,
  selections,
  ...rest
}: FrequentSelectionsProps) {
  const { selectionCounts } = useContext(SelectionCountsContext)
  const { theme } = useContext(ExplorerConfigContext)

  const currentSelections = options.map(o => makeSelection(o, groupTitle, getLanguageValue))
  let sortedSelections = selectionCounts
    .filter(s => getSelectionIndex(currentSelections, s) > -1)
    .sort((a, b) => b.count - a.count)
  if (sortedSelections.length > 5) sortedSelections = sortedSelections.slice(0, 5)

  if (!sortedSelections.length) return null

  return (
    <SView pt={8} {...rest}>
      <SText fontWeight="bold" color={theme.color.primary} fontSize={15}>
        Frequently Selected
      </SText>

      {sortedSelections.map((s, i) => {
        const option = options.find(o => getLanguageValue(o.title) === s.title)
        if (!option) return null

        return (
          <ListItem
            key={i}
            getLanguageValue={getLanguageValue}
            option={option}
            onSelect={() => onSelect(option)}
            isSelected={selections.some(s => s.title === getLanguageValue(option.title))}
          />
        )
      })}

      <SDivider mt={6} mr={20} />
    </SView>
  )
}

interface ListItemProps {
  getLanguageValue: ISessionContext['getLanguageValue']
  onSelect: () => void
  option: TraitValue
  isSelected: boolean
}

function ListItem({ isSelected, onSelect, option, getLanguageValue }: ListItemProps) {
  const { theme } = useContext(ExplorerConfigContext)
  return (
    <SView
      onClick={onSelect}
      flexShrink={0}
      borderRadius={8}
      cursor="pointer"
      direction="row"
      align="center"
      gap={16}
      h={listItemHeight}
    >
      {option.icon && <SKitIcon fontSize={40} type={option.icon} />}
      <SText>{getLanguageValue(option.title)}</SText>
      {isSelected && (
        <AnimatedCheckmark
          duration={250}
          checked
          size={listItemHeight * 0.8}
          stroke={theme.color.primary}
        />
      )}
    </SView>
  )
}

const listItemHeight = 30

function AlphaScroller({ onFilter }: { onFilter: (filter: string) => void }) {
  const { ref, height } = useElementSize<HTMLDivElement>()
  const letterHeight = height / alphabet.length - 2

  return (
    <SView
      cursor="pointer"
      justify="space-between"
      mb={panelPadding}
      mt={8}
      overflow="hidden"
      ref={ref}
    >
      {alphabet.map((letter, i) => (
        <SView
          key={i}
          align="center"
          onMouseDown={() => onFilter(letter)}
          // TODO - allow for scrolling to the correct letter
        >
          <SText fontSize={letterHeight}>{letter}</SText>
        </SView>
      ))}
    </SView>
  )
}

const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')
